import AddIcon from "@mui/icons-material/Add";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { colord } from "colord";

import { misc, primary } from "app/themes/colors";
import ProductPictureIcon from "components/product-picture-icon";
import { ProductCatalogOption } from "services/types/products.type";

import {
  getProductText,
  getSpecificationPreviewText
} from "../product-catalog-field.util";

type ProductListItemProps = {
  props: object;
  option: ProductCatalogOption;
  showComponentPrice?: boolean;
};

const inStockBgColor = colord(misc.mutedPurple).alpha(0.5).toHex();

export default function ProductListItem({
  props,
  option,
  showComponentPrice = false
}: ProductListItemProps) {
  const isInStockGroup = Boolean(
    option.serial_number && option.available_quantity
  );
  const isAddNew = option?.id === -1;
  const disabledReason = option?.add_new_disabled_reason;
  const isDisabledAddNew = Boolean(disabledReason);

  const label = (
    <Stack
      direction="row"
      alignItems="center"
      columnGap={1}
      data-testid={isAddNew ? "add-new-product-label" : undefined}
      sx={{
        fontSize: isAddNew ? "14px" : undefined,
        fontWeight: isAddNew ? 700 : undefined,
        color: isAddNew
          ? isDisabledAddNew
            ? misc.grayAsphalt
            : primary[400]
          : undefined,
        ...(isDisabledAddNew && {
          // MUI puts `pointer-events: none` on the disabled option itself, so
          // the tooltip below never fires unless the content re-enables them.
          pointerEvents: "auto",
          cursor: "not-allowed"
        })
      }}
    >
      {getProductText(option, showComponentPrice)}
      {isAddNew && <AddIcon sx={{ fontSize: "18px" }} />}
      <ProductPictureIcon
        productId={option.id}
        hasPicture={option.has_picture}
      />
    </Stack>
  );

  return (
    <ListItem
      {...props}
      key={`${option.id}-${option.inventory_id}-${option.serial_number}`}
      sx={{
        background: isInStockGroup ? inStockBgColor : undefined,
        mt: -0.5,
        // MUI fades a disabled option to `action.disabledOpacity` (0.38) — too
        // faint to read the one row whose whole job is to explain itself. Its
        // rule is `.listbox & .option[aria-disabled="true"]` (0,3,0), so a
        // plain `&[aria-disabled="true"]` (0,2,0) loses; `&&&` outranks it
        // regardless of stylesheet order.
        ...(isDisabledAddNew && {
          '&&&[aria-disabled="true"]': { opacity: 0.75 }
        })
      }}
      data-testid={
        isAddNew
          ? Boolean(disabledReason)
            ? "add-new-product-item-disabled"
            : "add-new-product-item"
          : "product-item"
      }
    >
      <Stack>
        <ListItemText
          disableTypography
          secondary={
            <Stack
              direction="row"
              sx={{ color: misc.grayAsphalt, mt: 0.5 }}
              spacing={1.5}
            >
              {Boolean(
                isInStockGroup &&
                  option.serial_number &&
                  option.available_quantity
              ) && (
                <Typography sx={{ pt: 0.25 }}>
                  Serial Number: {option.serial_number}
                </Typography>
              )}
              {getSpecificationPreviewText(option).map((text) => (
                <Typography key={text} sx={{ pt: 0.25 }}>
                  {text}
                </Typography>
              ))}
            </Stack>
          }
        >
          {/* Only the disabled add-row needs a tooltip; the catalog query
              returns up to 300 options and wrapping every one of them mounts
              300 Tooltips with their own listeners and ids for no reason. */}
          {isDisabledAddNew ? (
            <Tooltip title={disabledReason} placement="top">
              {label}
            </Tooltip>
          ) : (
            label
          )}
        </ListItemText>
      </Stack>
    </ListItem>
  );
}
